import * as THREE from 'three';
import {GLTFLoader} from 'three/examples/jsm/loaders/GLTFLoader';
import {
    LegsOffset,
    PreviewFormValues,
} from 'components/customer/Preview3D/types';
import {getGroupWidth} from 'components/customer/RoomPlanner/helpers/calculations';

/**
 * Calculates the toe kick scale based on the toe kick height.
 * @param {number | null} toeKickHeight - The height of the toe kick.
 * @return {number} The calculated scale value (defaults to 1 if toeKickHeight is null or 0).
 */
export const calculateToeKickScale = (toeKickHeight: number | null): number => {
    if (!toeKickHeight) {
        return 1;
    }
    const toeKickScale = Number((toeKickHeight / 150)?.toFixed(2));
    const toeKickYScale = toeKickScale + (toeKickScale - 1) * 0.01;
    return toeKickYScale;
};

/**
 * Represents adjustable legs functionality.
 * This class provides methods and properties to manage and manipulate adjustable legs.
 */
class AdjustibleLegs {
    private adjustableLegsQuantity = 5;
    private isLeftReturn = false;

    /**
     * Sets whether the cabinet has a left return.
     * @param {boolean} [isLeftReturn=false] - Indicates if the cabinet has a left return.
     */
    public setIsLeftReturn(isLeftReturn = false) {
        this.isLeftReturn = isLeftReturn;
    }

    /**
     * Transforms the adjustable legs model based on the specified type and cabinet values.
     * @param {'BOTTOM_LEFT' | 'TOP_RIGHT' | 'TOP_LEFT' | 'BOTTOM_RIGHT' | 'FRONT' | 'BACK'} type - The type of adjustable leg position.
     * @param {THREE.Group<THREE.Object3DEventMap>} model - The 3D model of the adjustable leg.
     * @param {PreviewFormValues} values - The form values containing cabinet dimensions and settings.
     * @param {number} scale - The scale of the adjustable leg.
     * @param {number} yOffset - Y axis adjustment for the adjustable leg
     * @return {THREE.Group<THREE.Object3DEventMap>} - The transformed 3D model of the adjustable leg.
     */
    private transformAdjustableLegsModel(
        type:
            | 'BOTTOM_LEFT'
            | 'TOP_RIGHT'
            | 'TOP_LEFT'
            | 'BOTTOM_RIGHT'
            | 'FRONT'
            | 'BACK',
        model: THREE.Group<THREE.Object3DEventMap>,
        values: PreviewFormValues,
        scale = 1,
        yOffset = 0
    ): THREE.Group<THREE.Object3DEventMap> {
        const degreesX = 90;
        const rotationX = (degreesX * Math.PI) / 180;
        const degreesY = 180;
        const rotationY = (degreesY * Math.PI) / 180;
        const modelScale = 750;
        const modelYScale = 1050;
        const legYScale = scale;
        const includeFaces =
            typeof values.include_drawer_faces !== 'undefined'
                ? Boolean(values.include_drawer_faces)
                : true;
        const voidWidth =
            values.cabinet_void_width &&
            Boolean(values.cabinet_cover_void) &&
            includeFaces
                ? parseFloat(values.cabinet_void_width?.toString())
                : 0;

        const multiplier = this.isLeftReturn ? -1 : 1;
        const widthForRightLegs = values.cabinet_width - voidWidth * multiplier;
        const widthForLeftLegs = values.cabinet_width + voidWidth * multiplier;
        const xPosition = (-(voidWidth / 2) - 33 * multiplier) * multiplier;

        model.scale.set(modelScale, modelYScale * legYScale, modelScale);

        const legHeight = getGroupWidth(model)?.height;
        model.position.z = -legHeight;
        model.rotation.x = rotationX;

        switch (type) {
            case 'TOP_RIGHT':
                model.rotation.y = rotationY;
                model.position.x = widthForRightLegs / 2;
                model.position.y = -(values.cabinet_depth / 2) + 100 + yOffset;
                break;
            case 'TOP_LEFT':
                model.position.x = -(widthForLeftLegs / 2);
                model.position.y = -(values.cabinet_depth / 2) + 35 + yOffset;
                break;
            case 'BOTTOM_RIGHT':
                model.rotation.y = rotationY;
                model.position.x = widthForRightLegs / 2;
                model.position.y = values.cabinet_depth / 2 - 35 - yOffset;
                break;
            case 'BACK':
                model.position.x = voidWidth !== 0 ? xPosition : -33;
                model.position.y = values.cabinet_depth / 2 - 110 - yOffset;
                break;
            case 'FRONT':
                model.position.x = voidWidth !== 0 ? xPosition : -33;
                model.position.y = -(values.cabinet_depth / 2) + 35 + yOffset;
                break;
            default:
                model.position.x = -(widthForLeftLegs / 2);
                model.position.y = values.cabinet_depth / 2 - 100 - yOffset;
                break;
        }

        return model;
    }

    /**
     * Adds adjustable legs to the cabinet model based on the provided values.
     * @param {THREE.Group<THREE.Object3DEventMap>} model - The 3D model of the adjustable leg.
     * @param {THREE.Group<THREE.Object3DEventMap>} cabinet - The 3D model of the cabinet.
     * @param {PreviewFormValues} values - The form values containing cabinet dimensions and settings.
     * @param {number} scale - The scale of the adjustable leg.
     * @param {LegsOffset | null} offset - Adjusts the y position of legs
     */
    private addAdjustableLegs(
        model: THREE.Group<THREE.Object3DEventMap>,
        cabinet: THREE.Group<THREE.Object3DEventMap>,
        values: PreviewFormValues,
        scale: number,
        offset?: LegsOffset
    ) {
        const legMeshGroup = new THREE.Group();
        const belowCabinetDepthThreshold = values.cabinet_depth >= 250;
        const cabinetWidth =
            values.cabinet_width + values.cabinet_ext_thickness;
        const conditionalWidth = 600;

        if (values.cabinet_depth >= 250) {
            const bottomLeftModel = this.transformAdjustableLegsModel(
                'BOTTOM_LEFT',
                model.clone(),
                values,
                scale,
                offset?.bottomLeft
            );
            legMeshGroup.add(bottomLeftModel);
        }

        const topRightModel = this.transformAdjustableLegsModel(
            'TOP_RIGHT',
            model.clone(),
            values,
            scale,
            offset?.topRight
        );
        legMeshGroup.add(topRightModel);

        const topLeftModel = this.transformAdjustableLegsModel(
            'TOP_LEFT',
            model.clone(),
            values,
            scale,
            offset?.topLeft
        );
        legMeshGroup.add(topLeftModel);

        if (belowCabinetDepthThreshold) {
            const bottomRightModel = this.transformAdjustableLegsModel(
                'BOTTOM_RIGHT',
                model.clone(),
                values,
                scale,
                offset?.bottomRight
            );
            legMeshGroup.add(bottomRightModel);
        }

        if (cabinetWidth >= conditionalWidth) {
            const frontModel = this.transformAdjustableLegsModel(
                'FRONT',
                model.clone(),
                values,
                scale,
                offset?.front
            );
            legMeshGroup.add(frontModel);
        }

        if (
            cabinetWidth >= conditionalWidth * 2 &&
            belowCabinetDepthThreshold
        ) {
            const backModel = this.transformAdjustableLegsModel(
                'BACK',
                model.clone(),
                values,
                scale,
                offset?.back
            );
            legMeshGroup.add(backModel);
        }

        legMeshGroup.position.y = values.cabinet_depth / 2;
        legMeshGroup.position.x = values.cabinet_width / 2;
        legMeshGroup.name = 'LegsGroup';
        cabinet.add(legMeshGroup);
    }

    /**
     * Transforms the L-shaped adjustable legs model based on the specified type and cabinet values.
     * @param {'BOTTOM_LEFT' | 'TOP_RIGHT' | 'TOP_LEFT' | 'BOTTOM_RIGHT' | 'BOTTOM_RIGHT_EXT' | 'TOP_RIGHT_EXT' | 'TOP_LEFT_EXT'} type - The type of adjustable leg position.
     * @param {THREE.Group<THREE.Object3DEventMap>} model - The 3D model of the adjustable leg.
     * @param {PreviewFormValues} values - The form values containing cabinet dimensions and settings.
     * @param {number} scale - The scale of the adjustable leg.
     * @return {THREE.Group<THREE.Object3DEventMap>} - The transformed 3D model of the adjustable leg.
     */
    private transformLShapedAdjustableLegsModel(
        type:
            | 'BOTTOM_LEFT'
            | 'TOP_RIGHT'
            | 'TOP_LEFT'
            | 'BOTTOM_RIGHT'
            | 'BOTTOM_RIGHT_EXT'
            | 'TOP_RIGHT_EXT'
            | 'TOP_LEFT_EXT',
        model: THREE.Group<THREE.Object3DEventMap>,
        values: PreviewFormValues,
        scale = 1
    ): THREE.Group<THREE.Object3DEventMap> {
        const degreesX = 90;
        const rotationX = (degreesX * Math.PI) / 180;
        const degreesY = 180;
        const rotationY = (degreesY * Math.PI) / 180;
        const modelScale = 750;
        const modelYScale = 1050;

        const cabinetRightWidth = Number(values.cabinet_right_width);
        const cabinetLeftWidth = Number(values.cabinet_left_width);
        const cabinetLeftDepth = Number(values.cabinet_left_depth);
        const cabinetRightDepth = Number(values.cabinet_right_depth);

        model.scale.set(modelScale, modelYScale * scale, modelScale);
        const legHeight = getGroupWidth(model)?.height;
        model.position.z = -legHeight;
        model.rotation.x = rotationX;

        switch (type) {
            case 'TOP_RIGHT':
                model.rotation.y = rotationY;
                model.position.x =
                    -(cabinetRightWidth / 2) + cabinetLeftDepth - 35;
                model.position.y = cabinetLeftWidth / 2 - cabinetRightDepth;
                100;
                break;
            case 'TOP_LEFT':
                model.rotation.y = (90 * Math.PI) / 180;
                model.position.x =
                    -(cabinetRightWidth / 2) + cabinetLeftDepth - 35;
                model.position.y = -cabinetLeftWidth / 2 - 10;
                break;
            case 'BOTTOM_RIGHT':
                model.rotation.y = -(90 * Math.PI) / 180;
                model.position.x = -(cabinetRightWidth / 2) + 20;
                model.position.y = cabinetLeftWidth / 2 + 10;
                break;
            case 'BOTTOM_RIGHT_EXT':
                model.rotation.y = -(90 * Math.PI) / 180;
                model.position.x = cabinetRightWidth / 2 - 60;
                model.position.y = cabinetLeftWidth / 2 - 20;
                break;
            case 'TOP_RIGHT_EXT':
                model.rotation.y = (90 * Math.PI) / 180;
                model.position.x = cabinetRightWidth / 2 + 10;
                model.position.y =
                    cabinetLeftWidth / 2 - cabinetRightDepth + 25;
                break;
            case 'TOP_LEFT_EXT':
                model.rotation.y = (90 * Math.PI) / 180;
                model.position.x =
                    -(cabinetRightWidth / 2) + cabinetLeftDepth + 50;
                model.position.y =
                    cabinetLeftWidth / 2 - cabinetRightDepth + 25;
                break;
            default:
                model.rotation.y = (90 * Math.PI) / 180;
                model.position.x = -(cabinetRightWidth / 2) + 90;
                model.position.y = -cabinetLeftWidth / 2 - 10;
                break;
        }

        return model;
    }

    /**
     * Adds L-shaped adjustable legs to the cabinet model based on the provided values.
     * @param {THREE.Group<THREE.Object3DEventMap>} model - The 3D model of the adjustable leg.
     * @param {THREE.Group<THREE.Object3DEventMap>} cabinet - The 3D model of the cabinet.
     * @param {PreviewFormValues} values - The form values containing cabinet dimensions and settings.
     * @param {number} scale - The scale of the adjustable leg.
     */
    private addLShapedAdjustableLegs(
        model: THREE.Group<THREE.Object3DEventMap>,
        cabinet: THREE.Group<THREE.Object3DEventMap>,
        values: PreviewFormValues,
        scale: number
    ) {
        const legMeshGroup = new THREE.Group();

        const bottomLeftModel = this.transformLShapedAdjustableLegsModel(
            'BOTTOM_LEFT',
            model.clone(),
            values,
            scale
        );
        bottomLeftModel.name = 'Leg-BOTTOM_LEFT';
        legMeshGroup.add(bottomLeftModel);

        const topRightModel = this.transformLShapedAdjustableLegsModel(
            'TOP_RIGHT',
            model.clone(),
            values,
            scale
        );
        topRightModel.name = 'Leg-TOP_RIGHT';
        legMeshGroup.add(topRightModel);

        const topLeftModel = this.transformLShapedAdjustableLegsModel(
            'TOP_LEFT',
            model.clone(),
            values
        );
        topLeftModel.name = 'Leg-TOP_LEFT';
        legMeshGroup.add(topLeftModel);

        const bottomRightModel = this.transformLShapedAdjustableLegsModel(
            'BOTTOM_RIGHT',
            model.clone(),
            values,
            scale
        );
        bottomRightModel.name = 'Leg-BOTTOM_RIGHT';
        legMeshGroup.add(bottomRightModel);

        const bottomRightExtModel = this.transformLShapedAdjustableLegsModel(
            'BOTTOM_RIGHT_EXT',
            model.clone(),
            values
        );
        bottomRightExtModel.name = 'Leg-BOTTOM_RIGHT_EXT';
        legMeshGroup.add(bottomRightExtModel);

        const topRightExtModel = this.transformLShapedAdjustableLegsModel(
            'TOP_RIGHT_EXT',
            model.clone(),
            values,
            scale
        );
        topRightExtModel.name = 'Leg-TOP_RIGHT_EXT';
        legMeshGroup.add(topRightExtModel);

        const topLeftExtModel = this.transformLShapedAdjustableLegsModel(
            'TOP_LEFT_EXT',
            model.clone(),
            values,
            scale
        );
        topLeftExtModel.name = 'Leg-TOP_LEFT_EXT';
        legMeshGroup.add(topLeftExtModel);

        legMeshGroup.position.y = values.cabinet_left_width / 2;
        legMeshGroup.position.x = values.cabinet_right_width / 2;
        legMeshGroup.name = 'LShape-LegsGroup';
        cabinet.add(legMeshGroup);
    }

    /**
     * Dynamically applies adjustable legs to the cabinet model based on the provided values.
     * @param {THREE.Group<THREE.Object3DEventMap>} cabinet - The 3D model of the cabinet.
     * @param {PreviewFormValues} values - The form values containing cabinet dimensions and settings.
     * @param {boolean} [isLShaped=false] - Indicates if the cabinet has L-shaped adjustable legs.
     * @param {number | null} toeKickHeight - The height of the toe kick.
     * @param {LegsOffset | null} offset - Adjusts the y position of legs
     */
    public applyDynamicAdjustableLegs(
        cabinet: THREE.Group<THREE.Object3DEventMap>,
        values: PreviewFormValues,
        isLShaped = false,
        toeKickHeight: number | null = null,
        offset?: LegsOffset
    ) {
        const loader = new GLTFLoader();
        const scale = calculateToeKickScale(toeKickHeight);

        loader.load('/templates/3D/models/adjustable_leg.glb', (gltf) => {
            const model = gltf.scene;
            if (!Boolean(values.cabinet_include_hardware)) {
                model.traverse(function (
                    child: THREE.Mesh & {
                        material: {
                            transparent: boolean;
                            opacity: number;
                        };
                    }
                ) {
                    if (child.isMesh && child.material) {
                        child.material.transparent = true;
                        child.material.opacity = 0.1;
                    }
                });
            }

            model.userData.ignoreOverlap = true;

            if (isLShaped) {
                this.addLShapedAdjustableLegs(model, cabinet, values, scale);
            } else {
                this.addAdjustableLegs(model, cabinet, values, scale, offset);
            }

            if (isLShaped) {
                const carc = Number(values.cabinet_carc_thickness ?? 0);
                const perLeg: Record<
                    string,
                    {x?: number; y?: number; z?: number}
                > = {
                    'Leg-BOTTOM_LEFT': {y: carc * 2},
                    'Leg-TOP_LEFT': {y: carc * 2},
                    'Leg-BOTTOM_RIGHT_EXT': {x: -carc},
                    'Leg-TOP_RIGHT_EXT': {x: -carc, y: carc + 4},
                    'Leg-TOP_LEFT_EXT': {y: carc + 4},
                };
                for (const child of cabinet.children) {
                    if (child.name !== 'LShape-LegsGroup') continue;
                    for (const leg of (
                        child as THREE.Group<THREE.Object3DEventMap>
                    ).children) {
                        const shift = perLeg[leg.name];
                        if (!shift) continue;
                        leg.position.x += shift.x ?? 0;
                        leg.position.y += shift.y ?? 0;
                        leg.position.z += shift.z ?? 0;
                    }
                }
            }
        });
    }
}

export default new AdjustibleLegs();
